import * as React from 'react';
import { Select, Spin } from 'antd';
import { ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'jalali-moment';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { TableColumn } from 'react-data-table-component';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from 'components/badge/badge';
import { Button, buttonType } from 'components/button/button';
import { Card } from 'components/card/card';
import { CopyablePhone } from 'components/copyable-phone/copyable-phone';
import { Input } from 'components/input/input';
import { Page } from 'components/page/page';
import { Table } from 'components/table/table';
import { UserLink } from 'components/user-link/user-link';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import {
    CampaignMember,
    CampaignReport as CampaignReportData,
    MarketingCampaignMemberReason,
    MarketingCampaignSmsStatus,
    PaginationList,
    campaignSmsBadges,
    memberReasonTitles,
} from 'core/domain';
import { campaignMembersGetter, getCampaignReport } from 'core/repositories/marketing';
import { jFormat, jFormatEndOfDay } from 'core/utils/jFormat';
import { BarSeriesChart, CHART_COLORS, MergedPoint } from '../../overview/charts/chart-commons';
import { faNum, formatDate, percent, toQuery } from '../marketing-utils';
import '../marketing.scss';

const { useEffect, useMemo, useState } = React;

const reasonOptions = Object.entries(memberReasonTitles).map(([value, label]) => ({ value: Number(value), label }));
const smsOptions = Object.entries(campaignSmsBadges).map(([value, badge]) => ({
    value: Number(value),
    label: badge.text,
}));

const chartSeries = [
    { key: 'notAuthenticatedCount', name: 'احراز نکرده', color: CHART_COLORS.amber },
    { key: 'notPurchasedCount', name: 'خرید نکرده', color: CHART_COLORS.blue },
    { key: 'authenticatedAfterCount', name: 'احراز کرد', color: CHART_COLORS.purple },
    { key: 'purchasedAfterCount', name: 'خرید کرد', color: CHART_COLORS.green },
];

const YesNo: React.FC<{ value: boolean }> = ({ value }) =>
    value ? <CheckOutlined style={{ color: CHART_COLORS.green }} /> : <CloseOutlined style={{ color: '#c0c6d4' }} />;

const Tile: React.FC<{ title: string; value: number; note?: string }> = ({ title, value, note }) => (
    <div className="mk-tile">
        <span>{title}</span>
        <strong>{faNum(value)}</strong>
        {note && <small>{note}</small>}
    </div>
);

interface MemberFilters {
    keyword: string;
    reason?: MarketingCampaignMemberReason;
    smsStatus?: MarketingCampaignSmsStatus;
}

export const CampaignReport: React.FC = () => {
    const navigate = useNavigate();
    const campaignId = Number(useParams().id);
    const getMembers = useMemo(() => campaignMembersGetter(campaignId), [campaignId]);

    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [report, setReport] = useState<CampaignReportData>();
    const [reportLoading, setReportLoading] = useState(false);

    const [memberFilters, setMemberFilters] = useState<MemberFilters>({ keyword: '' });
    const [members, setMembers] = useState<PaginationList<CampaignMember>>();
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersQuery, setMembersQuery] = useState('');
    const [pageSize, setPageSize] = useState(10);

    const dateParams = (nextFrom: string, nextTo: string) => ({
        startedDate: nextFrom ? jFormat(nextFrom) : '',
        endDate: nextTo ? jFormatEndOfDay(nextTo) : '',
    });

    const loadMembers = (filters: MemberFilters, nextFrom = from, nextTo = to) => {
        const query = toQuery({
            ...dateParams(nextFrom, nextTo),
            keyword: filters.keyword.trim(),
            reason: filters.reason,
            smsStatus: filters.smsStatus,
        });
        setMembersQuery(query);
        getMembers(setMembersLoading, setMembers, 1, pageSize, query);
    };

    const loadAll = async (nextFrom = from, nextTo = to) => {
        loadMembers(memberFilters, nextFrom, nextTo);
        const result = await getCampaignReport(setReportLoading, campaignId, toQuery(dateParams(nextFrom, nextTo)));
        if (result) setReport(result);
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    const chartData: MergedPoint[] = (report?.days ?? []).map((day) => {
        const jDate = moment(day.date, 'YYYY-MM-DD').locale('fa');
        return {
            label: jDate.format('MM/DD'),
            fullDate: jDate.format('YYYY/MM/DD'),
            notAuthenticatedCount: day.notAuthenticatedCount,
            notPurchasedCount: day.notPurchasedCount,
            authenticatedAfterCount: day.authenticatedAfterCount,
            purchasedAfterCount: day.purchasedAfterCount,
        };
    });

    const columns: TableColumn<CampaignMember>[] = [
        {
            name: 'کاربر',
            id: 'user',
            selector: (row) => row.phoneNumber ?? '',
            cell: (row) => (
                <div className="mk-user-cell">
                    <UserLink userId={row.userId}>{row.fullName?.trim() || 'بدون نام'}</UserLink>
                    <CopyablePhone phoneNumber={row.phoneNumber} />
                </div>
            ),
            minWidth: '170px',
        },
        {
            name: 'ثبت‌نام',
            id: 'registeredOn',
            selector: (row) => row.registeredOn,
            cell: (row) => <span>{formatDate(row.registeredOn)}</span>,
            minWidth: '130px',
        },
        {
            name: 'دلیل',
            id: 'reason',
            selector: (row) => row.reason,
            cell: (row) => <span>{memberReasonTitles[row.reason] ?? '-'}</span>,
            minWidth: '100px',
        },
        {
            name: 'ورود به کمپین',
            id: 'enrolledOn',
            selector: (row) => row.enrolledOn,
            cell: (row) => <span>{formatDate(row.enrolledOn)}</span>,
            minWidth: '130px',
        },
        {
            name: 'پیامک',
            id: 'smsStatus',
            selector: (row) => row.smsStatus,
            cell: (row) => {
                const badge = campaignSmsBadges[row.smsStatus];
                return badge ? <Badge text={badge.text} type={badge.type} /> : <span>-</span>;
            },
            width: '110px',
        },
        {
            name: 'احراز شده',
            id: 'isAuthenticated',
            selector: (row) => Number(row.isAuthenticated),
            cell: (row) => <YesNo value={row.isAuthenticated} />,
            width: '90px',
        },
        {
            name: 'خرید بعد از کمپین',
            id: 'purchasedAfter',
            selector: (row) => Number(row.purchasedAfter),
            cell: (row) => <YesNo value={row.purchasedAfter} />,
            width: '120px',
        },
        {
            name: 'تماس',
            id: 'called',
            selector: (row) => Number(row.called),
            cell: (row) => <YesNo value={row.called} />,
            width: '70px',
        },
    ];

    return (
        <Page className="pmx-marketing" title={report?.title ? `گزارش کمپین ${report.title}` : 'گزارش کمپین'}>
            <Card>
                <div className="mk-report">
                    <div className="mk-report__filters">
                        <DatePicker
                            value={from}
                            calendar={persian}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input value={from} icon={<CalendarIcon />} placeholder="از تاریخ" readOnly />
                                </div>
                            }
                            onChange={(date: any) => setFrom(date ? date.format() : '')}
                        />
                        <DatePicker
                            value={to}
                            calendar={persian}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input value={to} icon={<CalendarIcon />} placeholder="تا تاریخ" readOnly />
                                </div>
                            }
                            onChange={(date: any) => setTo(date ? date.format() : '')}
                        />
                        <Button
                            type={buttonType.success}
                            text="اعمال بازه"
                            disabled={reportLoading}
                            onClick={() => loadAll()}
                        />
                        <Button
                            type={buttonType.outlined}
                            text="بازگشت"
                            icon={<ArrowRightOutlined />}
                            onClick={() => navigate('/dashboard/marketing/campaigns')}
                        />
                    </div>

                    <Spin spinning={reportLoading}>
                        {report && (
                            <div className="mk-report">
                                <div className="mk-report__tiles">
                                    <Tile title="اعضای کمپین" value={report.totalMembers} />
                                    <Tile
                                        title="احراز نکرده"
                                        value={report.notAuthenticatedCount}
                                        note={percent(report.notAuthenticatedCount, report.totalMembers)}
                                    />
                                    <Tile
                                        title="خرید نکرده"
                                        value={report.notPurchasedCount}
                                        note={percent(report.notPurchasedCount, report.totalMembers)}
                                    />
                                    <Tile
                                        title="پیامک ارسال‌شده"
                                        value={report.smsSentCount}
                                        note={report.smsPendingCount ? `در صف ${faNum(report.smsPendingCount)}` : undefined}
                                    />
                                    <Tile
                                        title="پیامک ناموفق"
                                        value={report.smsFailedCount}
                                        note={percent(report.smsFailedCount, report.totalMembers)}
                                    />
                                    <Tile
                                        title="احراز کردند"
                                        value={report.authenticatedAfterCount}
                                        note={`${percent(report.authenticatedAfterCount, report.notAuthenticatedCount)} از احراز نکرده‌ها`}
                                    />
                                    <Tile
                                        title="بعد از کمپین خرید کردند"
                                        value={report.purchasedAfterCount}
                                        note={`${percent(report.purchasedAfterCount, report.totalMembers)} از اعضا`}
                                    />
                                    <Tile
                                        title="تماس گرفته‌شده"
                                        value={report.calledCount}
                                        note={percent(report.calledCount, report.totalMembers)}
                                    />
                                </div>

                                <div className="mk-report__section">
                                    <h4>روند روزانه (بر اساس روز ورود به کمپین)</h4>
                                    {chartData.length ? (
                                        <BarSeriesChart data={chartData} series={chartSeries} />
                                    ) : (
                                        <p className="mk-empty">در این بازه کسی وارد کمپین نشده.</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </Spin>

                    <div className="mk-report__section mk-panel">
                        <h4>اعضا</h4>
                        <div className="mk-panel__filters">
                            <Input
                                value={memberFilters.keyword}
                                placeholder="شماره موبایل یا نام کاربر"
                                onPressEnter={() => loadMembers(memberFilters)}
                                onChange={(e) => setMemberFilters({ ...memberFilters, keyword: e.target.value })}
                            />
                            <Select
                                allowClear
                                placeholder="دلیل"
                                value={memberFilters.reason}
                                options={reasonOptions}
                                onChange={(reason) => setMemberFilters({ ...memberFilters, reason })}
                            />
                            <Select
                                allowClear
                                placeholder="وضعیت پیامک"
                                value={memberFilters.smsStatus}
                                options={smsOptions}
                                onChange={(smsStatus) => setMemberFilters({ ...memberFilters, smsStatus })}
                            />
                            <div className="mk-filter-buttons">
                                <Button
                                    type={buttonType.success}
                                    text="جستجو"
                                    disabled={membersLoading}
                                    onClick={() => loadMembers(memberFilters)}
                                />
                            </div>
                        </div>
                        <Spin spinning={membersLoading}>
                            {members?.items?.length ? (
                                <Table
                                    blur={membersLoading}
                                    data={members.items}
                                    column={columns}
                                    pagination={members.pagination}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    setloader={setMembersLoading}
                                    setdata={setMembers}
                                    getdata={getMembers}
                                    filters={membersQuery}
                                />
                            ) : (
                                <p className="mk-empty">{membersLoading ? 'در حال بارگذاری...' : 'عضوی یافت نشد.'}</p>
                            )}
                        </Spin>
                    </div>
                </div>
            </Card>
        </Page>
    );
};
